import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FreightService, QuoteStatus } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  Filters,
  getPrettyQuoteStatus,
  getPrettySortName,
  modelUtils,
  quoteStatusOptions,
  SortNames,
} from '@pelicargo/utils';
import { uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  HiArrowPath,
  HiBarsArrowDown,
  HiBolt,
  HiBoltSlash,
  HiChevronDown,
  HiMiniArrowRightCircle,
  HiMiniFunnel,
  HiOutlineStar,
  HiPlus,
  HiStar,
  HiTrash,
} from 'react-icons/hi2';
import { useLocalStorage } from 'usehooks-ts';

import { trpc } from '../config/trpc';
import { useCurrentRequest } from '../hooks/useCurrentRequest';
import { quoteStatusColorScheme } from '../utils/constants';
import { Request } from '../utils/types';
import { Quote } from '../utils/types';
import { CreateQuoteModal } from './CreateQuoteModal';
import { InlandQuotesListHeader } from './Inland/InlandQuoteListHeader';
import { QuickSelect } from './QuickSelect';
import { RequestActiveUsers } from './RequestActiveUsers';
import { WithHotKey } from './WithHotKey';

type Props = {
  request: Request;
  quotes: Quote[];
  sort: string;
  freightService?: FreightService;
  setSort: (value: SortNames) => void;
  selectedQuoteIds: number[];
  setSelectedQuoteIds: (value: number[]) => void;
  handleSelectAllQuotes: () => void;
};

export const QuotesListHeader = ({
  request,
  quotes,
  sort,
  setSort,
  selectedQuoteIds,
  setSelectedQuoteIds,
  handleSelectAllQuotes,
  freightService,
}: Props) => {
  const toast = useToast();
  const createQuoteModalDisclosure = useDisclosure();
  const { mutateAsync: deleteQuotes, isLoading: isDeleting } = trpc.deleteQuotes.useMutation();
  const { mutateAsync: refreshApiQuotes, isLoading: isRefreshingApiQuotes } = trpc.refreshApiQuotes.useMutation();
  const { mutateAsync: setQuoteStatuses, isLoading: isChangingStatus } = trpc.setQuoteStatuses.useMutation();
  const { mutateAsync: autoSelect, isLoading: isSelecting } = trpc.autoSelectQuotes.useMutation();
  const { mutateAsync: connectInland, isLoading: isConnecting } = trpc.connectInlandQuoteVariants.useMutation();
  const { mutateAsync: disconnectInland, isLoading: isDisconnecting } =
    trpc.disconnectInlandQuoteVariants.useMutation();
  const { data: selectors } = trpc.getFilters.useQuery({
    scope: Filters.SCOPES.quoteSelector,
  });
  const isInland = quotes?.some(modelUtils.quote.isInland);
  const hasApiQuotes = quotes?.some((quote) => quote.is_api_generated) ?? false;
  const requiresInland = modelUtils.request.requiresInland(request);
  const afrQuotes = quotes?.filter(modelUtils.quote.isAfr);

  const handleChangeQuoteStatus = async (status: QuoteStatus) => {
    if (status === null) return;

    const { warnings } = await setQuoteStatuses({ quoteIds: selectedQuoteIds, status });
    if (warnings?.length) {
      toast({
        title: 'Warning',
        description: warnings?.join('\n'),
        status: 'warning',
      });
    }
    setSelectedQuoteIds([]);
  };

  const originAirportId = useMemo(() => {
    return request?.origin_airports?.[0]?.id;
  }, [request?.origin_airports]);

  const destinationAirportId = useMemo(() => {
    return request?.destination_airports?.[0]?.id;
  }, [request?.destination_airports]);

  const handleDeleteQuotes = useCallback(async () => {
    try {
      await deleteQuotes({ quoteIds: selectedQuoteIds });
      setSelectedQuoteIds([]);
      toast({ title: 'Success', description: 'Quotes deleted successfully', status: 'success' });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  }, [deleteQuotes, selectedQuoteIds, setSelectedQuoteIds, toast]);

  const handleRefreshApiQuotes = useCallback(async () => {
    try {
      const summary = await refreshApiQuotes({ request_id: request?.id });

      if (!summary.providers.length && !summary.failed.length) {
        toast({ title: 'No API quotes to refresh', status: 'info' });
        return;
      }

      const changes = [
        summary.replaced_variants && `${summary.replaced_variants} variant(s) updated`,
        summary.created_variants && `${summary.created_variants} variant(s) added`,
        summary.skipped && `${summary.skipped} skipped`,
        summary.failed.length && `${summary.failed.join(', ')} failed`,
      ].filter(Boolean);

      toast({
        title: summary.providers.length ? `Refreshed ${summary.providers.join(', ')}` : 'Refresh failed',
        description: changes.length ? changes.join(', ') : 'No changes',
        status: summary.failed.length ? 'warning' : 'success',
      });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  }, [refreshApiQuotes, request?.id, toast]);

  const quoteIds = useMemo(() => quotes?.map(({ id }) => id) || [], [quotes]);
  const someSelected = quoteIds.some((id) => selectedQuoteIds.includes(id));
  const allSelected = quoteIds?.length > 0 && quoteIds.every((id) => selectedQuoteIds.includes(id));

  const { inlandQuotes } = useCurrentRequest();
  const inlandQuoteVariants = useMemo(() => {
    return inlandQuotes?.flatMap((quote) => quote.quote_variants.map((variant) => ({ ...variant, quote }))) ?? [];
  }, [inlandQuotes]);

  const buttonBgColor = useColorModeValue('gray.100', 'gray.700');

  const options = [
    <option key="null" value={null}>
      -
    </option>,
    ...quoteStatusOptions.map((status) => (
      <option key={status.value} value={status.value}>
        {status.label}
      </option>
    )),
  ];
  const [favoriteSelector, setFavorite] = useLocalStorage<string>('favorite-selector', null);

  const doAutoSelect = useCallback(
    async (e) => {
      const selected = await autoSelect({
        request_id: request.id,
        identifier: String(e),
      });
      setSelectedQuoteIds(selected);
      toast({
        colorScheme: 'green',
        title: `Selected ${selected.length} quotes`,
      });
    },
    [autoSelect, request.id, setSelectedQuoteIds, toast],
  );

  const airportGroup = useMemo(() => {
    return inlandQuoteVariants?.reduce((acc, variant) => {
      const group = acc.find((group) => group.title === variant.quote.origin_airport_id);
      if (!group) {
        acc.push({ title: variant.quote.origin_airport_id, items: [variant] });
      } else {
        group.items.push(variant);
      }
      return acc;
    }, []);
  }, [inlandQuoteVariants]);

  const isAllAfrConnected = useMemo(() => {
    if (!afrQuotes?.length) return false;
    const qvs = afrQuotes.flatMap((quote) => quote.quote_variants);
    const allHaveConnections = qvs.every((qv) => qv.inland_service_quote_variant_id !== null);

    return allHaveConnections;
  }, [afrQuotes]);

  const handleDisconnectInland = useCallback(async () => {
    try {
      await disconnectInland({ request_id: request?.id });
      toast({
        title: 'Success',
        description: 'Inland connections disconnected successfully',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message,
        status: 'error',
      });
    }
  }, [disconnectInland, request?.id, toast]);

  const handleConnectInland = useCallback(
    async (inland_quote_variant_id: number) => {
      try {
        const updated = await connectInland({ inland_quote_variant_id, request_id: request?.id });
        if (updated?.length === 0) {
          toast({
            title: 'Warning',
            description: `No qualifying quotes to attach to. Is it a different FP or an unused airport?`,
            status: 'warning',
            duration: 5000,
          });
        } else {
          toast({
            title: 'Success',
            description: `Inland connection connected successfully (${updated?.length} variants attached)`,
            status: 'success',
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error?.message,
          status: 'error',
        });
      }
    },
    [connectInland, request?.id, toast],
  );

  return (
    <Stack spacing="0" mb="2">
      <Stack direction="row" p="4" justifyContent="space-between" w="full">
        <Stack direction="row" flex={1} pl="1">
          <Flex>
            <Checkbox
              as={Button}
              isIndeterminate={someSelected}
              isChecked={allSelected}
              w="max-content"
              bg={buttonBgColor}
              borderWidth={1}
              onChange={handleSelectAllQuotes}
              /* Chakra doesn't support shorthand like this, so we directly reference the var names.
            Specific things like "borderRight" get passed to the checkbox and it is ugly */
              borderRadius="var(--chakra-radii-md) 0 0 var(--chakra-radii-md)"
            >
              {someSelected || allSelected ? 'Unselect All' : 'Select All'}
            </Checkbox>
            <QuickSelect
              size="sm"
              nextValue={{
                label: favoriteSelector ?? selectors?.[0]?.identifier,
                value: favoriteSelector ?? selectors?.[0]?.identifier,
              }}
              buttonProps={{
                minW: '140px',
                textAlign: 'left',
                alignItems: 'center',
                width: '100%',
                bg: buttonBgColor,
                borderLeftRadius: 0,
                leftIcon: isSelecting ? <Spinner size="xs" /> : <HiMiniFunnel />,
              }}
              onChange={doAutoSelect}
              type="menu"
              dropdownHotkey="f"
            >
              <MenuButton
                as={Button}
                borderWidth={1}
                borderLeftWidth={0}
                rightIcon={<HiChevronDown />}
                isLoading={isSelecting}
                borderLeftRadius={0}
                paddingInlineStart={1}
                flex={1}
              />
              <MenuList>
                <MenuItem onClick={handleSelectAllQuotes}>
                  {someSelected || allSelected ? 'Unselect All' : 'Select All'}
                </MenuItem>
                {selectors?.map(({ identifier }) => (
                  <Flex key={identifier}>
                    <MenuItem as="div" onClick={() => doAutoSelect(identifier)} justifyContent="space-between">
                      {identifier}
                      <IconButton
                        size="xs"
                        variant="ghost"
                        icon={
                          favoriteSelector === identifier ? <HiStar color="orange" /> : <HiOutlineStar color="gray" />
                        }
                        aria-label="favorite"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFavorite(identifier);
                        }}
                      />
                    </MenuItem>
                  </Flex>
                ))}
              </MenuList>
            </QuickSelect>
          </Flex>
          <Show if={someSelected || allSelected}>
            <Stack direction="row">
              <QuickSelect
                size="sm"
                nextValue={{
                  value: QuoteStatus.InProgress,
                  label: getPrettyQuoteStatus(QuoteStatus.InProgress),
                }}
                buttonProps={{
                  minW: '140px',
                  textAlign: 'left',
                  alignItems: 'center',
                  width: '100%',
                  bg: buttonBgColor,
                  leftIcon: <HiMiniArrowRightCircle />,
                }}
                onChange={handleChangeQuoteStatus}
                isLoading={isChangingStatus}
              >
                {options}
              </QuickSelect>
              <Button
                size="sm"
                leftIcon={<HiTrash color="red" />}
                bg={buttonBgColor}
                borderWidth={1}
                isLoading={isDeleting}
                onClick={handleDeleteQuotes}
                _focus={{
                  borderColor: 'var(--select-bg)',
                }}
              >
                Remove
              </Button>
            </Stack>
          </Show>
        </Stack>
        <Stack direction="row" alignItems="center" justify="flex-end" spacing={2} w="full">
          <RequestActiveUsers requestId={request?.id} />
          <Stack direction="row" align="center">
            <Text fontSize="xs" opacity="0.6" noOfLines={1}>
              Sort By:
            </Text>
            <Select
              value={sort}
              w="fit-content"
              onChange={(e) => setSort(e.target.value as SortNames)}
              size="sm"
              bg={buttonBgColor}
              borderWidth={1}
              icon={<HiBarsArrowDown />}
            >
              <option value="created_at">{getPrettySortName('created_at')}</option>
              <option value="cost">{getPrettySortName('cost')}</option>
              <option value="cost_total">{getPrettySortName('cost_total')}</option>
              <option value="origin">{getPrettySortName('origin')}</option>
              <option value="destination">{getPrettySortName('destination')}</option>
              <option value="provider_id">{getPrettySortName('provider_id')}</option>
            </Select>
          </Stack>
          <Show if={requiresInland}>
            <Menu id="inland-connections">
              <WithHotKey
                type="menu"
                dropdownHotkey="i"
                as={Button}
                variant="ghost"
                leftIcon={
                  isAllAfrConnected ? <Icon as={HiBolt} color="success" /> : <Icon as={HiBoltSlash} color="muted" />
                }
                bg={buttonBgColor}
                textAlign="left"
                isLoading={isConnecting || isDisconnecting}
                width="fit-content"
                id="inland-connections"
              >
                Inland Connections
              </WithHotKey>
              <MenuList maxH="80vh" overflowY="auto">
                <Stack divider={<Divider />}>
                  {airportGroup?.map((group) => (
                    <MenuGroup key={group.title} title={group.title}>
                      {group.items?.map((variant) => {
                        const isConnected = !!uniqBy(variant.quote_variants_for_inland_service, 'quote_id').length;
                        return (
                          <MenuItem key={variant?.id} onClick={() => handleConnectInland(variant?.id)}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              w="full"
                              spacing={4}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Show if={isConnected}>
                                  <Icon as={HiBolt} color="success" />
                                </Show>
                                <Show if={!isConnected}>
                                  <Icon as={HiBoltSlash} color="muted" />
                                </Show>
                                <Text maxW="350px" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                  {variant?.quote?.provider?.name}
                                </Text>
                              </Stack>
                              <Tag
                                variant="outline"
                                colorScheme={quoteStatusColorScheme[variant?.quote?.status]}
                                fontWeight="500"
                                size="md"
                                w="fit-content"
                              >
                                {getPrettyQuoteStatus(variant?.quote?.status)}
                              </Tag>
                            </Stack>
                          </MenuItem>
                        );
                      })}
                    </MenuGroup>
                  ))}
                  <MenuGroup title="Disconnect">
                    <MenuItem onClick={handleDisconnectInland}>Disconnect All</MenuItem>
                  </MenuGroup>
                </Stack>
              </MenuList>
            </Menu>
          </Show>
          <Flex>
            <WithHotKey
              type="button"
              dropdownHotkey="n"
              rightIcon={<HiPlus />}
              aria-label="Add New Quote"
              bg={buttonBgColor}
              borderWidth={1}
              borderRightWidth={0}
              borderRightRadius={0}
              onClick={createQuoteModalDisclosure.onOpen}
            >
              New Quote
            </WithHotKey>
            <Menu id="quote-actions">
              <MenuButton
                as={IconButton}
                aria-label="Quote actions"
                icon={<HiChevronDown />}
                bg={buttonBgColor}
                borderWidth={1}
                borderLeftRadius={0}
                isLoading={isRefreshingApiQuotes}
              />
              <MenuList>
                <MenuItem
                  icon={<HiArrowPath />}
                  isDisabled={!hasApiQuotes || isRefreshingApiQuotes}
                  onClick={handleRefreshApiQuotes}
                >
                  Refresh API Quotes
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Stack>
        <Show if={createQuoteModalDisclosure.isOpen}>
          <CreateQuoteModal
            request={request}
            initialOriginAirportId={originAirportId}
            initialDestinationAirportId={destinationAirportId}
            freightService={freightService}
            {...createQuoteModalDisclosure}
          />
        </Show>
      </Stack>
      <Show if={isInland}>
        <InlandQuotesListHeader request={request} />
      </Show>
    </Stack>
  );
};
